import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import SiteLayout from "@/components/feature/SiteLayout";
import ComicCard from "@/components/feature/ComicCard";
import ChapterList from "@/pages/detail/components/ChapterList";
import type { Chapter } from "@/types/comic";
import { formatCount, getComicById, relatedComics } from "@/utils/comics";

export default function DetailPage() {
  const { comicId } = useParams();
  const comic = getComicById(comicId);
  const [introExpanded, setIntroExpanded] = useState(false);
  const [toast, setToast] = useState("");
  const [chapterNotice, setChapterNotice] = useState("");

  useEffect(() => {
    setIntroExpanded(false);
    setChapterNotice("");
  }, [comicId]);

  useEffect(() => {
    if (!toast) {
      return undefined;
    }
    const timer = window.setTimeout(() => setToast(""), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!chapterNotice) {
      return undefined;
    }
    const timer = window.setTimeout(() => setChapterNotice(""), 2600);
    return () => window.clearTimeout(timer);
  }, [chapterNotice]);

  if (!comic) {
    return (
      <SiteLayout>
        <PageMeta
          title="漫画未找到 - JMComic漫画频道"
          description="未找到对应的漫画作品，请返回 JMComic 首页继续浏览精选漫画资源。"
          keywords="JMComic,漫画未找到,漫画平台"
          path={`/detail/${comicId ?? ""}`}
        />
        <div className="mx-auto flex w-full max-w-[1240px] flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-book-open-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">漫画未找到</h1>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-500">
            这部漫画可能已下架或链接有误。回到首页看看其他精选作品吧。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Link
              to="/"
              className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-home-5-line"></i>
              返回首页
            </Link>
            <Link
              to="/category/all"
              className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700"
            >
              浏览全部漫画
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = relatedComics(comic, 4);
  const shareUrl = `${window.location.origin}${window.location.pathname}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setToast("链接已复制，可以分享给朋友啦");
    } catch {
      setToast("复制失败，请手动复制浏览器地址");
    }
  };

  const openShare = (platform: "weibo" | "x") => {
    const title = `${comic.title} - JMComic漫画`;
    const url =
      platform === "weibo"
        ? `https://service.weibo.com/share/share.php?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(title)}`
        : `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleChapterClick = (chapter: Chapter) => {
    setChapterNotice(`《${chapter.title}》阅读页正在开发中，敬请期待`);
  };

  return (
    <SiteLayout>
      <PageMeta
        title={`${comic.title} - JMComic漫画详情 | 精彩连载尽览`}
        description={`查看${comic.title}详细介绍、作者信息及最新章节，尽在JMComic精选漫画阅读平台。`}
        keywords={`${comic.title},${comic.author},${comic.categoryName}漫画,JMComic,漫画详情`}
        path={`/detail/${comic.id}`}
      />

      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-4 md:px-6">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${comic.categoryId}`} className="hover:text-primary-700">
              {comic.categoryName}漫画
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">{comic.title}</span>
          </nav>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1240px] px-4 pt-6 md:px-6 md:pt-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 sm:flex-row md:gap-5 md:p-5">
              <div className="h-[220px] w-[165px] shrink-0 overflow-hidden rounded-md bg-secondary-100 sm:h-[260px] sm:w-[195px]">
                <img
                  src={comic.cover}
                  alt={`${comic.title}漫画封面`}
                  title={`${comic.title} - JMComic漫画详情`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                      comic.status === "连载"
                        ? "bg-primary-500 text-background-50"
                        : "bg-secondary-900 text-background-50"
                    }`}
                  >
                    {comic.status}
                  </span>
                  <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[10px] font-medium text-accent-800">
                    {comic.categoryName}
                  </span>
                  <span className="text-[11px] text-foreground-400">{comic.region}</span>
                </div>

                <h1 className="mt-2 font-heading text-2xl font-extrabold leading-tight tracking-tight text-foreground-950 md:text-3xl">
                  {comic.title}
                </h1>
                <p className="mt-1.5 text-sm text-foreground-600">
                  作者：<span className="font-medium text-foreground-900">{comic.author}</span>
                </p>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {comic.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>

                <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                  最新章节：<span className="font-medium text-primary-700">{comic.latestChapter}</span>
                  <span className="mx-2 text-background-400">|</span>
                  更新于 {comic.updatedAt}
                </p>

                <div className="mt-3 grid grid-cols-3 gap-2">
                  {[
                    { label: "总章节", value: `${comic.chapterCount} 话` },
                    { label: "累计阅读", value: formatCount(comic.viewCount) },
                    { label: "收藏", value: formatCount(comic.likeCount) },
                  ].map((item) => (
                    <div key={item.label} className="rounded-md bg-background-100 px-2.5 py-2">
                      <span className="block font-heading text-sm font-bold text-foreground-950">
                        {item.value}
                      </span>
                      <span className="block text-[10px] text-foreground-500">{item.label}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-auto flex flex-wrap gap-2 pt-4">
                  <button
                    type="button"
                    onClick={() => setChapterNotice("章节阅读页正在开发中，当前可先浏览详情与章节列表")}
                    className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-book-read-line"></i>
                    开始阅读
                  </button>
                  <Link
                    to={`/category/${comic.categoryId}`}
                    className="flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    更多{comic.categoryName}漫画
                  </Link>
                </div>
              </div>
            </div>

            <section className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-file-text-line text-lg text-primary-500"></i>
                作品简介
              </h2>
              <p
                className={`mt-3 text-sm leading-[1.9] text-foreground-600 ${
                  introExpanded ? "" : "line-clamp-2-safe"
                }`}
              >
                {comic.intro}
              </p>
              <button
                type="button"
                onClick={() => setIntroExpanded((value) => !value)}
                className="mt-3 flex items-center gap-1 whitespace-nowrap text-xs font-medium text-primary-700 hover:text-primary-800"
              >
                {introExpanded ? "收起简介" : "展开完整简介"}
                <i className={`${introExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
              </button>
            </section>

            <div className="mt-4">
              <ChapterList comic={comic} onSelect={handleChapterClick} />
            </div>

            <section className="mt-8">
              <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
                <div>
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.16em] text-primary-600">
                    You May Also Like
                  </span>
                  <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
                </div>
                <Link
                  to={`/category/${comic.categoryId}`}
                  className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-foreground-500 hover:text-primary-700"
                >
                  查看同类作品
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
                {related.map((item) => (
                  <ComicCard key={item.id} comic={item} />
                ))}
              </div>
            </section>
          </div>

          <aside className="space-y-4 lg:col-span-4">
            <div className="sticky top-[84px] space-y-4">
              <section className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="font-heading text-sm font-bold text-foreground-950">作品信息</h2>
                <dl className="mt-3 space-y-2.5 text-xs">
                  {[
                    { label: "作者", value: comic.author },
                    { label: "题材", value: comic.categoryName },
                    { label: "地区", value: comic.region },
                    { label: "状态", value: comic.status },
                    { label: "章节", value: `共 ${comic.chapterCount} 话` },
                    { label: "更新时间", value: comic.updatedAt },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start justify-between gap-3">
                      <dt className="shrink-0 text-foreground-400">{item.label}</dt>
                      <dd className="text-right font-medium text-foreground-800">{item.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="flex items-center gap-1.5 font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-share-forward-line text-base text-accent-600"></i>
                  分享这部漫画
                </h2>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={copyLink}
                    className="flex flex-col items-center gap-1.5 rounded-md border border-background-200 py-3 text-[11px] font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-link text-lg"></i>
                    复制链接
                  </button>
                  <button
                    type="button"
                    onClick={() => openShare("weibo")}
                    className="flex flex-col items-center gap-1.5 rounded-md border border-background-200 py-3 text-[11px] font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-weibo-line text-lg"></i>
                    微博
                  </button>
                  <button
                    type="button"
                    onClick={() => openShare("x")}
                    className="flex flex-col items-center gap-1.5 rounded-md border border-background-200 py-3 text-[11px] font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-twitter-x-line text-lg"></i>
                    X
                  </button>
                </div>
                <p className="mt-2.5 text-[10px] leading-relaxed text-foreground-400">
                  分享仅供交流漫画信息，请支持正版阅读。
                </p>
              </section>

              <section className="rounded-lg border border-primary-200 bg-primary-50 p-4">
                <h2 className="font-heading text-sm font-bold text-primary-900">同类题材推荐</h2>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {related.slice(0, 3).map((item) => (
                    <Link
                      key={item.id}
                      to={`/detail/${item.id}`}
                      className="rounded-full border border-primary-200 bg-background-50 px-2.5 py-1 text-[11px] font-medium text-primary-800 transition-colors hover:border-primary-400"
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
                <Link
                  to="/updates"
                  className="mt-3 flex h-9 items-center justify-center gap-1 rounded-md bg-primary-500 text-xs font-semibold text-background-50 hover:bg-primary-600"
                >
                  查看今日连载更新
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </section>
            </div>
          </aside>
        </div>
      </div>

      {(toast || chapterNotice) && (
        <div className="animate-rise fixed bottom-20 left-1/2 z-[70] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 rounded-md bg-foreground-900 px-4 py-3 text-center text-xs font-medium text-background-50 md:bottom-8">
          {toast || chapterNotice}
        </div>
      )}
    </SiteLayout>
  );
}